/**
 * Build a PATH for plugin install-hook execution that includes the standard
 * Homebrew + system bin directories. The manager process is often spawned
 * by cloud-init under a minimal PATH that omits `/home/linuxbrew/.linuxbrew/bin`,
 * so a hook script that calls `npm`, `npx`, `qmd`, `xurl`, etc. exits 127
 * ("command not found") even though the binaries are installed.
 *
 * Order:
 *   1. The current process PATH (operator overrides win)
 *   2. Linuxbrew prefix (EC2 / Linux hosts)
 *   3. macOS Apple-silicon brew prefix
 *   4. Intel macOS / generic /usr/local
 *   5. Standard system bins, in case the inherited PATH was empty.
 *
 * Callers should pass `process.env.PATH` so an existing operator-augmented
 * PATH is preserved at the front.
 */
export function augmentedHookPath(currentPath: string | undefined): string {
  const extras = [
    '/home/linuxbrew/.linuxbrew/bin',
    '/opt/homebrew/bin',
    '/usr/local/bin',
    '/usr/bin',
    '/bin',
  ];
  const seen = new Set<string>();
  const parts: string[] = [];
  const push = (p: string): void => {
    if (!p || seen.has(p)) return;
    seen.add(p);
    parts.push(p);
  };
  for (const p of (currentPath ?? '').split(':')) push(p);
  for (const p of extras) push(p);
  return parts.join(':');
}

/**
 * When a hook exits 127, bash itself prints `bash: line N: <cmd>: command not found`
 * to stderr before any user code runs, so the message does not contain
 * secrets the script may have echoed. Extract the first such line so logs
 * can show the missing binary without leaking the rest of stderr.
 *
 * Returns null if no recognisable not-found line is present (e.g. the
 * script returned 127 by itself for some other reason).
 */
export function extractCommandNotFound(stderr: string): string | null {
  if (!stderr) return null;
  // Restrict to shells we actually invoke (`bash -c`/`sh -c`) so an attacker
  // can't craft a fake `evil-shell: foo: command not found` line in script
  // stdout that gets reflected through to logs. The captured token is logged
  // raw, so validate it is a basename-style command name: starts with a
  // letter, charset limited to alnum + `._-`, max 64 chars. This keeps the
  // leakage surface tight even if the regex above lets something unexpected
  // through.
  const SAFE_CMD = /^[A-Za-z][A-Za-z0-9._-]{0,63}$/;
  for (const line of stderr.split(/\r?\n/)) {
    const m = line.match(/^(?:bash|sh): (?:line \d+: )?([^:\s]+): command not found$/);
    if (m?.[1]) {
      // Defensive: strip any path prefix before validating, so a future
      // change to the line regex can't accidentally let a full path leak.
      const rawCmd = m[1].trim();
      const cmd = rawCmd.split('/').pop() ?? rawCmd;
      if (SAFE_CMD.test(cmd)) return cmd;
    }
  }
  return null;
}
